import React from 'react';
import {
  Avatar,
  Badge,
  Flex,
  Hint,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  Link,
  Separator,
  Skeleton,
  Text,
} from '@/components/ui';
import { useUserCellViewMode } from './useUserCellViewMode';
import type { UserCellViewMode } from './userCellPreferences';
import { IconInfoCircle } from '@tabler/icons-react';
import { useAuth } from '@/contexts/AuthContext';

interface UserCellProps {
  value?: unknown;
  user?: {
    id: string | number;
    first_name?: string;
    last_name?: string;
    name?: string;
    email?: string;
    avatar?: string;
  };
  showEmail?: boolean;
  size?: 'small' | 'medium' | 'large';
  render?: () => React.ReactNode;
  disableHoverCard?: boolean;
  viewMode?: UserCellViewMode; // Override global preference
}

function UserCell({
  value,
  user,
  showEmail = true,
  render,
  disableHoverCard = false,
  viewMode: forcedViewMode,
}: UserCellProps) {
  // Get current user for comparison
  const { user: currentUser } = useAuth();

  // Use global hook for synchronized view mode across all UserCell components
  const { viewMode: globalViewMode, setViewMode } = useUserCellViewMode();

  // Use forced viewMode if provided, otherwise use global preference
  const viewMode = forcedViewMode ?? globalViewMode;
  const isViewModeForced = forcedViewMode !== undefined;

  if (render) {
    return <>{render()}</>;
  }

  // Fallback: if value is a string, just display it as text
  if (typeof value === 'string' && value.trim() !== '') {
    return (
      <Text size="2xsmall" weight="medium">
        {value}
      </Text>
    );
  }

  // Extract user data from value if user prop is not provided
  const userData = user || (value as UserCellProps['user']);

  if (!userData) {
    return (
      <Text size="2xsmall" color="gray">
        Unknown user
      </Text>
    );
  }

  // Handle different user object structures
  const firstName = userData.first_name || '';
  const lastName = userData.last_name || '';
  const email = userData.email || '';
  const name = userData.name;

  const fullName = name || `${firstName} ${lastName}`.trim() || 'Unknown';

  // Check if this is the current user
  const isCurrentUser =
    currentUser &&
    userData.id !== undefined &&
    String(userData.id) === String(currentUser.id);

  // Base display name without "(You)" suffix
  const baseDisplayName = fullName || email || 'Unknown';
  const displayName = baseDisplayName;
  const avatarSrc = userData.avatar;
  const avatarAlt = displayName;
  const initials =
    firstName && lastName
      ? `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase()
      : baseDisplayName.slice(0, 2).toUpperCase();

  // Reusable "You" badge component
  const youBadge = isCurrentUser ? (
    <Badge
      size="xsmall"
      color="default"
      variant="outlined"
      style={{
        margin: 'calc(var(--space-2) * -1) 0',
        marginLeft: 'var(--space-1) !important',
      }}
    >
      You
    </Badge>
  ) : null;

  // Handle click to toggle view mode (only if not forced)
  const handleClick = () => {
    if (isViewModeForced) {
      return; // Don't allow toggling when viewMode is forced
    }
    const newMode = viewMode === 'compact' ? 'full' : 'compact';
    setViewMode(newMode);
  };

  // Hover card content
  const hoverCardContent = (
    <Flex
      direction="column"
      gap="3"
      style={{ width: '100%', minWidth: '300px', maxWidth: '340px' }}
    >
      <Flex align="center" gap="2">
        <Avatar variant="surface" src={avatarSrc} alt={avatarAlt} size="small">
          {initials}
        </Avatar>
        <Flex direction="column" gap="0">
          <Flex align="center" gap="2" wrap="wrap">
            <Text size="xsmall" weight="medium">
              {displayName}
            </Text>
            {youBadge}
          </Flex>
          {email && (
            <Link
              href={`mailto:${email}`}
              size="2"
              color="indigo"
              onClick={e => e.stopPropagation()}
            >
              {email}
            </Link>
          )}
        </Flex>
      </Flex>
      <Separator />
      {!isViewModeForced && (
        <Hint icon={<IconInfoCircle />} color="gray">
          Click to change view
        </Hint>
      )}
    </Flex>
  );

  // Cell content based on view mode
  const cellContent =
    viewMode === 'compact' ? (
      <Flex
        align="center"
        gap="2"
        className="data-cell__user"
        style={{
          cursor: isViewModeForced ? 'default' : 'pointer',
          minWidth: 0,
          flex: 1,
        }}
        onClick={handleClick}
      >
        {showEmail && email ? (
          <Flex align="center" gap="2" style={{ minWidth: 0, flex: 1 }}>
            <Text
              size="2xsmall"
              className="hasHoverCard"
              truncate={true}
              title={email}
              style={{ minWidth: 0, flex: 1 }}
            >
              {email}
            </Text>
            {youBadge}
          </Flex>
        ) : displayName && displayName !== 'Unknown' ? (
          <>
            <Avatar
              variant="surface"
              src={avatarSrc}
              alt={avatarAlt}
              size="xsmall"
            >
              {initials}
            </Avatar>
            <Flex align="center" gap="2" style={{ minWidth: 0, flex: 1 }}>
              <Text
                size="2xsmall"
                className="hasHoverCard"
                style={{ minWidth: 0, flex: 1 }}
              >
                {displayName}
              </Text>
              {youBadge}
            </Flex>
          </>
        ) : (
          <Text size="2xsmall" color="gray" className="hasHoverCard">
            Unknown user
          </Text>
        )}
      </Flex>
    ) : (
      <Flex
        align="center"
        gap="2"
        className="data-cell__user"
        style={{ cursor: isViewModeForced ? 'default' : 'pointer' }}
        onClick={handleClick}
      >
        <Avatar variant="surface" src={avatarSrc} alt={avatarAlt} size="xsmall">
          {initials}
        </Avatar>
        <Flex direction="column" gap="1" style={{ minWidth: 0, flex: 1 }}>
          <Flex align="center" gap="2" wrap="wrap">
            <Text size="2" weight="medium" style={{ minWidth: 0, flex: 1 }}>
              {displayName}
            </Text>
            {youBadge}
          </Flex>
          {showEmail && email && (
            <Text
              size="3xsmall"
              color="gray"
              truncate={true}
              title={email}
              style={{ minWidth: 0 }}
            >
              {email}
            </Text>
          )}
        </Flex>
      </Flex>
    );

  // Wrap with HoverCard if not disabled
  if (disableHoverCard) {
    return cellContent;
  }

  return (
    <HoverCardRoot>
      {/* @ts-expect-error - asChild is supported by Radix UI but not in types */}
      <HoverCardTrigger asChild>{cellContent}</HoverCardTrigger>
      <HoverCardContent size="2">{hoverCardContent}</HoverCardContent>
    </HoverCardRoot>
  );
}

export function UserCellSkeleton() {
  return (
    <Flex align="center" gap="2">
      <Skeleton
        style={{ width: '32px', height: '32px', borderRadius: '50%' }}
      />
      <Flex direction="column" gap="1">
        <Skeleton
          style={{ width: '120px', height: '14px', borderRadius: '4px' }}
        />
        <Skeleton
          style={{ width: '100px', height: '12px', borderRadius: '4px' }}
        />
      </Flex>
    </Flex>
  );
}

export default UserCell;
